import React, { useEffect, useMemo } from "react";
import Styles from "./HistoryList.module.scss"
import {useAppDispatch} from "../../../../redux/store";
import {useSelector} from "react-redux";
import preloader from "../../../../assets/img/load.gif";
import HallsItem from "../HallsItem/HallsItem";
import {fetchPurchaseHistory} from "../../../../redux/profile/purchaseHistory/asyncActions";
import SubscriptionItem from "../SubscriptionsItem/SubscriptionItem";
import {getPurchaseHistory} from "../../../../redux/profile/purchaseHistory/selectors";
import { subscriptionsSelector } from "../../../../redux/subscriptions/selectors";
import { IPurchaseItem, IPurchaseSubscriptionItem, IPurchaseRentItem } from "../../../../redux/profile/purchaseHistory/types";
import { getProfileInfoMenu } from "../../../../redux/profile/profileInfo/selectors";
import { partnerIdSelector, userDataSelector } from "../../../../redux/user/selectors";

const HistoryList: React.FC = () => {
    const dispatch = useAppDispatch();
    const {purchaseItems, isLoading} = useSelector(getPurchaseHistory);
    const { subscriptions } = useSelector(subscriptionsSelector);
    const { myProfiles } = useSelector(getProfileInfoMenu);
    const userPartnerId = useSelector(partnerIdSelector);
    const userData = useSelector(userDataSelector);

    useEffect(() => {
        dispatch(fetchPurchaseHistory());
    }, []);

    const getTodayDate = (): Date => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    };

    const getExpiredHistory = (): IPurchaseItem[] => {
        const today = getTodayDate();
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        sixMonthsAgo.setHours(0, 0, 0, 0);
        
        return purchaseItems
            .filter((item): item is IPurchaseItem => {
                if (item.type === 'rent') {
                    const finishTime = item.finishTime || item.date;
                    if (!finishTime) return false;
                    const itemDate = new Date(finishTime);
                    itemDate.setHours(0, 0, 0, 0);
                    return itemDate < today && itemDate >= sixMonthsAgo;
                }
                if (item.type === 'subscription') {
                    const finishTime = item.date_to_iso || item.expires_at || item.finishTime;
                    if (!finishTime) return false;
                    const itemDate = new Date(finishTime);
                    itemDate.setHours(0, 0, 0, 0);
                    return itemDate < today && itemDate >= sixMonthsAgo;
                }
                return false;
            })
            .sort((a, b) => {
                const getDate = (item: IPurchaseItem): number => {
                    if (item.type === 'rent') {
                        return new Date(item.finishTime || item.date || 0).getTime();
                    } else {
                        return new Date(item.date_to_iso || item.expires_at || item.finishTime || 0).getTime();
                    }
                };
                return getDate(b) - getDate(a);
            });
    };

    const historyExpired = useMemo(() => getExpiredHistory(), [purchaseItems]);

    const adaptSubscriptionData = (item: IPurchaseSubscriptionItem) => {
        const itemPartnerId = item.partnerId || item.partner_id;
        let ownerName = item.profile.name;
        
        if (itemPartnerId) {
            if (userPartnerId && String(userPartnerId) === String(itemPartnerId)) {
                if (userData?.name) {
                    ownerName = userData.name;
                }
            } else if (myProfiles && myProfiles.length > 0) {
                const matchingProfile = myProfiles.find((profile: any) => {
                    const profilePartnerId = profile.partner_id;
                    if (!profilePartnerId) return false;
                    return String(profilePartnerId) === String(itemPartnerId);
                });
                
                if (matchingProfile) {
                    ownerName = matchingProfile.name;
                }
            }
        }
        
        return {
            id: typeof item.id === 'string' ? parseInt(item.id) || 0 : (item.id as number) || 0,
            price: item.price,
            bought_at: item.bought_at || item.date_from_iso || '',
            created_at: item.date_from_iso || '',
            date_from: item.date_from_iso || '',
            date_to: item.date_to_iso || '',
            date_from_iso: item.date_from_iso || '',
            date_to_iso: item.date_to_iso || '',
            expiration_time: item.expires_at || item.finishTime || '',
            expires_at: item.expires_at || item.finishTime || '',
            expired: false,
            extension_prohibited: false,
            has_discount: item.isDiscountPeriod || false,
            order_id: 0,
            paid: 1,
            profile_id: 0,
            pushed_at: undefined,
            quantity: 1,
            user_id: 0,
            user_notified: 0,
            discount: 0,
            exp_bak_11252020: null,
            updated_at: item.date_to_iso || '',
            subscription_id: item.subscription_id || (typeof item.membershipId === 'string' ? parseInt(item.membershipId) : item.membershipId) || 0,
            membershipId: item.membershipId,
            isDiscountPeriod: item.isDiscountPeriod,
            additionalOwners: item.additionalOwners,
            subscription: {
                id: item.subscription_id || item.subscription.id || 0,
                title: item.subscription.title,
                includes: item.subscription.includes,
                secondary_title: '',
                type: item.subscription.type,
                price: item.subscription.price,
                created_at: null,
                updated_at: null,
                external_id: '',
                active: 1,
                is_new_version: 0,
                expiration_text: '',
                discount_text: '',
                cart_expiration_text: '',
                expiration_days: 30,
                has_discount: item.isDiscountPeriod ? 1 : 0,
                sort_order: 0
            },
            profile: {
                id: 0,
                user_id: undefined,
                unique: undefined,
                type: 'adult',
                name: ownerName,
                phone: undefined,
                birthday: undefined,
                created_at: '',
                updated_at: '',
                is_self: 0,
                deleted: 0,
                phone_1c_format_not_needed: undefined
            },
            family: item.family ? item.family.map((f: { profile: { name: string } }, idx: number) => ({
                id: idx,
                paid: 1,
                subscription_id: item.subscription_id || 0,
                expiration_time: item.expires_at || item.finishTime || '',
                user_id: 0,
                price: item.price,
                profile_id: 0,
                quantity: 1,
                order_id: 0,
                pushed_at: null,
                created_at: item.date_from_iso || '',
                updated_at: '',
                user_notified: 0,
                discount: 0,
                exp_bak_11252020: null,
                expired: false,
                date_from: item.date_from_iso || '',
                date_to: item.date_to_iso || '',
                expires_at: item.expires_at || '',
                date_to_iso: item.date_to_iso || '',
                date_from_iso: item.date_from_iso || '',
                has_discount: false,
                bought_at: item.bought_at || '',
                extension_prohibited: false,
                profile: {
                    id: idx,
                    user_id: null,
                    unique: null,
                    type: 'adult',
                    name: f.profile.name,
                    phone: null,
                    birthday: null,
                    created_at: '',
                    updated_at: '',
                    is_self: 0,
                    deleted: 0,
                    phone_1c_format_not_needed: null
                },
                subscription: {
                    id: item.subscription_id || item.subscription.id || 0,
                    title: item.subscription.title,
                    includes: item.subscription.includes,
                    secondary_title: '',
                    type: item.subscription.type,
                    price: item.subscription.price,
                    created_at: null,
                    updated_at: null,
                    external_id: '',
                    active: 1,
                    is_new_version: 0,
                    expiration_text: '',
                    discount_text: '',
                    cart_expiration_text: '',
                    expiration_days: 30,
                    has_discount: 0,
                    sort_order: 0
                }
            })) : undefined
        };
    };

    const adaptRentData = (item: IPurchaseRentItem) => {
        return {
            checked: 1,
            created_at: item.created_at || new Date().toISOString(),
            date: item.date || item.finishTime || '',
            hall: {
                active: 1,
                created_at: null,
                id: item.hall.id,
                image_url: '',
                price: item.price,
                price_10: 0,
                price_11: 0,
                price_12: 0,
                price_13: 0,
                price_14: 0,
                price_15: 0,
                price_16: 0,
                price_17: 0,
                price_18: 0,
                price_19: 0,
                price_20: 0,
                price_21: 0,
                price_22: 0,
                price_23: 0,
                price_7: 0,
                price_8: 0,
                price_9: 0,
                prime_price: item.price,
                title: item.hall.title,
                updated_at: ''
            },
            hall_id: item.hall.id,
            id: typeof item.id === 'string' ? parseInt(item.id) || 0 : (item.id as number) || 0,
            ip: '',
            order_id: 0,
            paid: 1,
            price: item.price,
            reserved_until: 0,
            time: item.time || '',
            time_to: item.time_to || '',
            token: null,
            unique: '',
            updated_at: new Date().toISOString(),
            user_id: 0
        };
    };

    if (isLoading) {
        return (
        <div className={Styles.wrapper}>
                    <div className={Styles.preloader}>
                        <img className={Styles.preloader_img} src={preloader} alt="preloader" />
                    </div>
            </div>
        );
    }

    return (
        <div className={Styles.wrapper}>
            {historyExpired.length === 0 ? (
                <div className={Styles.emptyState}>
                    <p className={Styles.emptyText}>
                        У вас нет истекших заказов
                    </p>
                </div>
                ) : (
                    <div className={Styles.item}>
                    {historyExpired.map((item, index) => {
                        if (item.type === "subscription") {
                            return (
                                <SubscriptionItem 
                                    key={item.id || `subscription-${index}`}
                                    status="big" 
                                    data={adaptSubscriptionData(item)}
                                    subscriptions={subscriptions}
                                />
                            );
                        } else {
                            return (
                                <HallsItem 
                                    key={item.id || `hall-${index}`}
                                    data={adaptRentData(item)} 
                                />
                            );
                        }
                    })}
                </div>
                        )}
                    </div>
    );
}

export default HistoryList;
